'use client'

import { useCallback, useState } from 'react'
import type { PaymentMethod } from '@/root/shared-types/reservation/reservation-types'
import reservationService from '@/root/shared-api/reservation/reservationService'
import {
  canCompleteWithoutPaymentRedirect,
  getEnabledPaymentMethodsInDisplayOrder,
} from '@/root/shared-helpers/payment/payment-helpers'
import { useGetProfileData } from '@/root/shared-hooks/useGetProfileData/useGetProfileData'
import { fetchPaymentMethods, revert } from '@/app/actions/reservation-modification'
import type { Locale } from '@/root/shared-types'
import type { Key } from '@/root/shared-hooks/useGetTranslations/useGetTranslations'
import { AxiosError } from 'axios'
import { logger } from '@/logger'
import { handleStaleServerAction } from '../utils'

interface UsePaymentFlowParams {
  locale: Locale
  reservationToken?: string
  onError: (message: string | null) => void
  onRedirect: (path: string) => void
  onFinishingChange: (finishing: boolean) => void
  getTranslation: (key: Key) => string
  toPayAmount: number
  ownerCompanyId?: number
}

interface PaymentStatus {
  isActive: boolean
  isProcessing: boolean
  isCancelling: boolean
  canPay: boolean
}

interface PaymentSelection {
  methods: PaymentMethod[]
  enabledMethods: PaymentMethod[]
  creditBalance: number
  isCreditBalanceLoading: boolean
  isCreditInsufficient: boolean
  selectedMethod: PaymentMethod | null
  selectMethod: (method: PaymentMethod) => void
  isTermsAccepted: boolean
  toggleTerms: () => void
}

interface PaymentActions {
  fetchAndProcessPayment: (reservationId: number, committedToken: string) => Promise<void>
  pay: (reservationId: number) => Promise<void>
  cancel: () => Promise<void>
}

export interface UsePaymentFlowReturn {
  status: PaymentStatus
  selection: PaymentSelection
  actions: PaymentActions
  showPaymentUI: () => void
}

export function usePaymentFlow({
  locale,
  reservationToken,
  onError,
  onRedirect,
  onFinishingChange,
  getTranslation,
  toPayAmount,
  ownerCompanyId,
}: UsePaymentFlowParams): UsePaymentFlowReturn {
  const buildErrorMessage = useCallback(
    (message: string, code?: string) => {
      const errorCodeLabel = getTranslation('errorRedCodePrefix')
      return code ? `${message} ${errorCodeLabel} ${code}` : message
    },
    [getTranslation]
  )

  const [isPaymentMode, setIsPaymentMode] = useState(false)
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null)
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false)
  const [isPaymentCancelling, setIsPaymentCancelling] = useState(false)
  const [isTermsAccepted, setIsTermsAccepted] = useState(false)
  const [committedToken, setCommittedToken] = useState<string | null>(null)

  const enabledMethods = getEnabledPaymentMethodsInDisplayOrder(paymentMethods)
  const hasCreditMethod = enabledMethods.some((method) => method.code === 'CREDIT')
  // The profile is read only when the backend offered CREDIT on the payment step
  const shouldReadProfile = isPaymentMode && hasCreditMethod
  const { companies, isLoading: isProfileLoading } = useGetProfileData(shouldReadProfile)
  const isCreditBalanceLoading = shouldReadProfile && isProfileLoading
  const ownerCompany =
    ownerCompanyId === undefined ? undefined : companies.find((company) => company.companyId === ownerCompanyId)
  const creditBalance = ownerCompany?.creditCompany ? ownerCompany.creditLimit - ownerCompany.used : 0
  const isCreditInsufficient = creditBalance < toPayAmount
  const isPaymentBusy = isPaymentProcessing || isPaymentCancelling
  const canPay =
    selectedPaymentMethod !== null &&
    isTermsAccepted &&
    !isPaymentBusy &&
    !(selectedPaymentMethod.code === 'CREDIT' && isCreditInsufficient)

  const pay = useCallback(
    async (reservationId: number) => {
      if (!selectedPaymentMethod || !reservationToken) return
      setIsPaymentProcessing(true)
      onError(null)

      const redirectToken = committedToken ?? reservationToken

      try {
        const result = await reservationService.preparePayment({
          reservationId,
          paymentMethod: selectedPaymentMethod.code,
          origin: window.origin,
          creditCard: selectedPaymentMethod.code === 'CARD',
          locale,
          flow: 'modify',
        })

        if (result.redirectUrl) {
          window.location.href = result.redirectUrl
        } else if (canCompleteWithoutPaymentRedirect(selectedPaymentMethod)) {
          onRedirect(`/${locale}/ticket/${redirectToken}`)
        } else {
          // Nothing was paid: the ticket page would show the change as done while it stays unpaid
          logger.error('prepare-payment returned no redirect URL for a method paid at the provider', {
            reservationId,
            paymentMethod: selectedPaymentMethod.code,
            transactionId: result.transactionId,
          })
          onError(buildErrorMessage(getTranslation('editReservationPaymentError')))
          setIsPaymentProcessing(false)
        }
      } catch (err) {
        if (handleStaleServerAction({ error: err, getTranslation, onError })) {
          setIsPaymentProcessing(false)
          return
        }

        const axiosErr = err instanceof AxiosError ? err : null
        onError(
          buildErrorMessage(
            axiosErr?.response?.data?.message ?? getTranslation('editReservationPaymentError'),
            axiosErr?.response?.data?.code
          )
        )
        setIsPaymentProcessing(false)
      }
    },
    [
      selectedPaymentMethod,
      reservationToken,
      committedToken,
      locale,
      onError,
      onRedirect,
      getTranslation,
      buildErrorMessage,
    ]
  )

  const cancel = useCallback(async () => {
    if (!reservationToken) return
    setIsPaymentCancelling(true)
    onError(null)

    try {
      const revertResult = await revert(reservationToken, locale)
      if (!revertResult.success) {
        onError(getTranslation('editErrorCancelFailed'))
        setIsPaymentCancelling(false)
        return
      }

      onRedirect(`/${locale}/ticket/${reservationToken}`)
    } catch (err) {
      if (handleStaleServerAction({ error: err, getTranslation, onError })) {
        setIsPaymentCancelling(false)
        return
      }

      onError(getTranslation('editErrorCancelFailed'))
      setIsPaymentCancelling(false)
    }
  }, [reservationToken, locale, onError, onRedirect, getTranslation])

  // Fetch payment methods and store them for the payment UI.
  const fetchAndProcessPayment = useCallback(
    async (reservationId: number, token: string): Promise<void> => {
      if (!reservationToken) return
      setCommittedToken(token)

      let methodsResult: Awaited<ReturnType<typeof fetchPaymentMethods>>
      try {
        methodsResult = await fetchPaymentMethods(reservationId)
      } catch (err) {
        if (handleStaleServerAction({ error: err, getTranslation, onError })) {
          onFinishingChange(false)
          return
        }

        throw err
      }

      if (!methodsResult.success) {
        onError(
          buildErrorMessage(
            methodsResult.error.message ?? getTranslation('editReservationPaymentNoMethods'),
            methodsResult.error.code
          )
        )
        onFinishingChange(false)
        return
      }

      setPaymentMethods(methodsResult.data)
    },
    [reservationToken, onError, onFinishingChange, getTranslation, buildErrorMessage]
  )

  return {
    status: {
      isActive: isPaymentMode,
      isProcessing: isPaymentProcessing,
      isCancelling: isPaymentCancelling,
      canPay,
    },
    selection: {
      methods: paymentMethods,
      enabledMethods,
      creditBalance,
      isCreditBalanceLoading,
      isCreditInsufficient,
      selectedMethod: selectedPaymentMethod,
      selectMethod: setSelectedPaymentMethod,
      isTermsAccepted,
      toggleTerms: () => setIsTermsAccepted((prev) => !prev),
    },
    actions: {
      fetchAndProcessPayment,
      pay,
      cancel,
    },
    showPaymentUI: useCallback(() => {
      setIsPaymentMode(true)
      onFinishingChange(false)
    }, [onFinishingChange]),
  }
}
